/**
 * Reusable form utility functions for handling checkbox arrays and other common operations
 */

/**
 * Handle checkbox array toggle (add/remove items)
 * @param {Object} methods - React Hook Form methods object
 * @param {string} fieldName - Name of the array field
 * @param {string} value - Value to toggle
 * @param {Object} options - Optional configuration
 * @param {string} options.dependentField - Name of the dependent field to clear when value is unchecked
 * @param {string} options.dependentValue - The specific value that triggers dependent field clearing (default: "other")
 * @param {any} options.clearValue - The value to set when clearing dependent field (default: "")
 * @param {Function} handleValueChanges - Callback to update parent state
 * 
 * @example
 * // Clear "other" field when "other" checkbox is unchecked
 * handleCheckboxArrayToggle(methods, "languages", "other", 
 *   { dependentField: "languages_other" }, handleValueChanges)
 * 
 * @example
 * // Clear dependent field when specific value is unchecked
 * handleCheckboxArrayToggle(methods, "options", "custom", 
 *   { dependentField: "custom_details", dependentValue: "custom" }, handleValueChanges)
 * 
 * @example
 * // No dependent field
 * handleCheckboxArrayToggle(methods, "platforms", "ios", null, handleValueChanges)
 */
export const handleCheckboxArrayToggle = (
  methods,
  fieldName,
  value,
  options = null,
  handleValueChanges
) => {
  let updated = [...methods.getValues(fieldName)];
  
  if (updated.includes(value)) {
    // Remove the value
    updated = updated.filter((item) => item !== value);
    
    // Clear the dependent field if configured and value matches
    if (options?.dependentField) {
      const triggerValue = options.dependentValue || "other";
      const clearValue = options.clearValue !== undefined ? options.clearValue : "";
      
      if (value === triggerValue) {
        methods.setValue(options.dependentField, clearValue);
      }
    }
  } else {
    // Add the value
    updated.push(value);
  }
  
  methods.setValue(fieldName, updated);
  methods.trigger();
  handleValueChanges();
};

/**
 * Handle input field change
 * @param {Object} methods - React Hook Form methods object
 * @param {string} fieldName - Name of the field
 * @param {any} value - New value
 * @param {Function} handleValueChanges - Callback to update parent state
 */
export const handleInputChange = (methods, fieldName, value, handleValueChanges) => {
  methods.setValue(fieldName, value, { shouldValidate: true });
  handleValueChanges();
};

/**
 * Handle radio button change
 * @param {Object} methods - React Hook Form methods object
 * @param {string} fieldName - Name of the field
 * @param {any} value - New value
 * @param {Object} fieldsToReset - Optional: Object with field names as keys and reset values
 * @param {Function} handleValueChanges - Callback to update parent state
 */
export const handleRadioChange = (
  methods,
  fieldName,
  value,
  fieldsToReset = {},
  handleValueChanges
) => {
  methods.setValue(fieldName, value);
  
  // Reset other fields if provided
  Object.entries(fieldsToReset).forEach(([field, resetValue]) => {
    methods.setValue(field, resetValue);
  });
  
  methods.trigger();
  handleValueChanges();
};

/**
 * Clear all conditional fields based on a condition
 * @param {Object} methods - React Hook Form methods object
 * @param {Object} fieldsToReset - Object with field names as keys and reset values
 * @param {Function} handleValueChanges - Callback to update parent state
 */
export const clearConditionalFields = (methods, fieldsToReset, handleValueChanges) => {
  Object.entries(fieldsToReset).forEach(([field, resetValue]) => {
    methods.setValue(field, resetValue);
  });
  
  methods.trigger();
  handleValueChanges();
};
